import { apiTryFetchRetry } from '@editor/utils/apiFetchRetry';
import analytics from '@repo/analytics';

import { WarpGrid } from '../components/MockupBoard/types';
import { SEARCH_MOCKUPS } from '../global/analytics/events';
import { GenericCmsResponse } from '../types';
import { Mockup, MockupGroup } from '../types/Mockup';

import { CMS_API_URL } from './cmsContent';

type GetResponse = GenericCmsResponse<{ mockup: Mockup }>;
type OverviewResponse = GenericCmsResponse<{
  mockups: {
    [key: string]: MockupGroup &
      {
        mockups: Mockup[];
        total: number;
        skip: number;
        take: number;
      }[];
  };
}>;
type GroupResponse = GenericCmsResponse<{
  mockups: Mockup[];
  skip: number;
  take: number;
  sort: string | null;
}>;
type SearchResponse = GenericCmsResponse<{
  mockups: Mockup[];
  skip: number;
  take: number;
  sort: string | null;
}>;
type SuggestionsResponse =
  | {
      tags: string[];
      groups: string[];
    }
  | {
      success: false;
      error: string;
    };
type CreateResponse = GenericCmsResponse<{ mockup: { id: number } }>;

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string | undefined }) => {
  const get = async (id: number): Promise<GetResponse> => {
    return apiTryFetchRetry<GetResponse>(`${url}/${id}`);
  };

  const getOverview = async (): Promise<OverviewResponse> => {
    return apiTryFetchRetry<OverviewResponse>(`${url}/overview`);
  };

  const getGroup = async (
    groupId: string,
    take = 10,
    skip = 0,
    sort = 'name'
  ): Promise<GroupResponse> => {
    return await apiTryFetchRetry<GroupResponse>(
      `${url}/groups/${groupId}?take=${take}&skip=${skip}&sort=${sort}`
    );
  };

  const search = async (
    query: string,
    take = 10,
    skip = 0,
    sort = 'name'
  ): Promise<SearchResponse> => {
    if (skip === 0) {
      analytics.track(SEARCH_MOCKUPS, {
        label: query,
      });
    }
    return await apiTryFetchRetry<SearchResponse>(
      `${url}/search?query=${query}&take=${take}&skip=${skip}&sort=${sort}`
    );
  };

  const getSuggestions = async (): Promise<SuggestionsResponse> => {
    return await apiTryFetchRetry<SuggestionsResponse>(`${url}/suggestions`);
  };

  // store the elements as ids, for which the use was registered
  const usedMockups: number[] = [];

  const registerUse = async (id: number): Promise<boolean> => {
    // register the use of elements only once
    if (usedMockups.includes(id)) {
      return false;
    }
    usedMockups.push(id);

    const response = await apiTryFetchRetry<GenericCmsResponse>(`${url}/use`, {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    return response.success;
  };

  const create = async ({
    name,
    preview,
    sceneLayer,
    darkBlendLayer,
    lightBlendLayer,
    warpGrid,
  }: {
    name: string | null;
    preview: string;
    sceneLayer: File;
    darkBlendLayer?: File | null;
    lightBlendLayer?: File | null;
    warpGrid?: WarpGrid | null;
  }): Promise<CreateResponse> => {
    const body = new FormData();
    body.append('preview', preview);
    body.append('sceneLayer', sceneLayer);

    name && body.append('name', name);
    darkBlendLayer && body.append('darkBlendLayer', darkBlendLayer);
    lightBlendLayer && body.append('lightBlendLayer', lightBlendLayer);
    warpGrid && body.append('warpGrid', JSON.stringify(warpGrid));

    return apiTryFetchRetry<CreateResponse>(`${url}/create`, {
      method: 'POST',
      body,
    });
  };

  const update = async ({
    id,
    name,
    preview,
    sceneLayer,
    darkBlendLayer,
    lightBlendLayer,
    warpGrid,
  }: {
    id: number;
    name: string | null;
    preview: string;
    sceneLayer: File | null;
    darkBlendLayer?: File | null;
    lightBlendLayer?: File | null;
    warpGrid?: WarpGrid | null;
  }): Promise<CreateResponse> => {
    const body = new FormData();
    body.append('preview', preview);

    name && body.append('name', name);
    sceneLayer && body.append('sceneLayer', sceneLayer);
    darkBlendLayer && body.append('darkBlendLayer', darkBlendLayer);
    lightBlendLayer && body.append('lightBlendLayer', lightBlendLayer);
    warpGrid && body.append('warpGrid', JSON.stringify(warpGrid));

    return apiTryFetchRetry<CreateResponse>(`${url}/${id}`, {
      method: 'PUT',
      body,
    });
  };

  return {
    get,
    getOverview,
    getGroup,
    search,
    getSuggestions,
    registerUse,

    // mockup management
    create,
    update,
  };
};

const mockupApi = createAPI({ url: `${CMS_API_URL}/api/mockups` });
export default mockupApi;
